import { Box, Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PaginatedTable from "../../components/dynamicTable";
import AppLoader from "../../components/appLoader";
import { openSnackAlert } from "../../helper";
import { getAllArchivedWorkers, unarchiveWorker } from "../../services/Workers";
import useWorkerStore from "../../zustand/useWorkerStore";

const employeeTableHeaders = [
  { title: "EMPLOYEE ID", value: "employeeId", align: "center" },
  { title: "NAME", value: "workerName", align: "center" },
  { title: "DESIGNATION", value: "designation", align: "center" },
  { title: "DEPARTMENT", value: "department", align: "center" },
  { title: "EMAIL", value: "email", align: "center" },
  { title: "CHECK IN TIME", value: "checkInTime", align: "center" },
  { title: "CHECK OUT TIME", value: "checkOutTime", align: "center" },
  { title: "ARCHIVED", value: "archived", align: "center" },
  // { title: "STATUS", value: "workerStatus", align: "center" },
  { title: "View Details", value: "viewWorkerDetail", align: "center" },
];

const displayRows = employeeTableHeaders.map((header) => header.value);

const ArchivedEmployes = () => {
  const { refreshWorkerData } = useWorkerStore();
  const navigate = useNavigate();
  const [allArchivedWorkers, setAllArchivedWorkers] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchArchivedWorkers = async () => {
    try {
      setApiLoading(true);
      const response = await getAllArchivedWorkers();
      const resData = response?.data;

      if (resData?.status === "success") {
        const workers =
          resData?.data?.workers ||
          resData?.data?.archivedWorkers ||
          resData?.data ||
          [];

        const archivedWorkers = Array.isArray(workers) ? workers : [];
        setAllArchivedWorkers(archivedWorkers);
        setTotalRecords(archivedWorkers.length);
      } else {
        openSnackAlert(
          resData?.message || "Failed to fetch archived workers",
          "error"
        );
      }
    } catch (error) {
      openSnackAlert(error?.message || "Something went wrong", "error");
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    fetchArchivedWorkers();
  }, []);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageLimit(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleViewWorkerDetail = (row) => {
    navigate("/employee-profile", { state: { employeeId: row._id } });
  };

  // In archived page, the `archived` switch should unarchive the employee.
  const handleArchiveWorker = async (_e, row) => {
    try {
      setApiLoading(true);
      const response = await unarchiveWorker(row._id);
      const resData = response?.data;

      if (resData?.status === "success" || response?.status === 200 || response?.status === 201) {
        openSnackAlert(resData?.message || "Worker unarchived successfully", "success");
        await fetchArchivedWorkers();
        await refreshWorkerData();
      } else {
        openSnackAlert(resData?.message || "Error unarchiving worker", "error");
      }
    } catch (error) {
      openSnackAlert(error?.message || "Something went wrong while unarchiving worker", "error");
    } finally {
      setApiLoading(false);
    }
  };

  const paginatedWorkers = useMemo(() => {
    const start = currentPage * pageLimit;
    const end = start + pageLimit;
    return allArchivedWorkers.slice(start, end);
  }, [allArchivedWorkers, currentPage, pageLimit]);

  return (
    <Box
      bgcolor="primary.bgLight"
      padding={"20px"}
      borderRadius={"22px"}
      mt={0}
    >
      <AppLoader isVisible={apiLoading} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Typography variant="h4">Archived Employee</Typography>
          <Typography variant="description">
            List of all archived employees
          </Typography>
        </Grid>
      </Grid>

      <Box mt={1}>
        <PaginatedTable
          tableHeader={employeeTableHeaders}
          tableData={paginatedWorkers}
          displayRows={displayRows}
          rowsPerPage={pageLimit}
          currentPage={currentPage}
          totalRecords={totalRecords}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          handleViewWorkerDetail={handleViewWorkerDetail}
          handleArchiveWorker={handleArchiveWorker}
        />
      </Box>
    </Box>
  );
};

export default ArchivedEmployes;